import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { admin } from '../../lib/api';
import { Button } from '../../ui/button';
import { Checkbox, CheckboxField } from '../../ui/checkbox';
import { ErrorMessage, Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Text } from '../../ui/text';
import { Panel } from '../../components/PageChrome';
import { useCurrentTeam } from './AdminLayout';
import type { TeamUniform } from '../../types';

interface FormState {
  name: string;
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  season: string;
  city: string;
  state: string;
  leagueName: string;
  division: string;
  homeFieldName: string;
  homeFieldAddress: string;
  contactEmail: string;
  contactPhone: string;
  adminNotes: string;
  active: boolean;
}

const emptyForm: FormState = {
  name: '',
  logoUrl: '',
  primaryColor: '#10b981',
  secondaryColor: '#f59e0b',
  season: '',
  city: '',
  state: '',
  leagueName: '',
  division: '',
  homeFieldName: '',
  homeFieldAddress: '',
  contactEmail: '',
  contactPhone: '',
  adminNotes: '',
  active: true,
};

interface UniformFormState {
  name: string;
  jerseyColor: string;
  pantsColor: string;
  hatColor: string;
  socksColor: string;
  beltColor: string;
  notes: string;
}

const emptyUniformForm: UniformFormState = {
  name: '',
  jerseyColor: '#ffffff',
  pantsColor: '#ffffff',
  hatColor: '#111827',
  socksColor: '#111827',
  beltColor: '#111827',
  notes: '',
};

const nullable = (value: string) => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

function UniformSwatches({ uniform }: { uniform: TeamUniform }) {
  const swatches = [
    uniform.jerseyColor,
    uniform.pantsColor,
    uniform.hatColor,
    uniform.socksColor,
    uniform.beltColor,
  ].filter(Boolean);
  return (
    <div className="flex items-center gap-1">
      {swatches.length > 0 ? (
        swatches.map((color, index) => (
          <span
            key={`${color}-${index}`}
            className="size-5 rounded border border-white/20"
            style={{ backgroundColor: color }}
          />
        ))
      ) : (
        <span className="text-xs text-zinc-500">No colors</span>
      )}
    </div>
  );
}

export default function TeamDetails() {
  const { team, teamId, loading, error: loadError, refresh } = useCurrentTeam();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>();
  const [savedAt, setSavedAt] = useState<string>();
  const [rotating, setRotating] = useState(false);
  const [rotated, setRotated] = useState<string>();
  const [rotateError, setRotateError] = useState<string>();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();
  const [uniforms, setUniforms] = useState<TeamUniform[]>([]);
  const [uniformForm, setUniformForm] = useState<UniformFormState>(emptyUniformForm);
  const [savingUniform, setSavingUniform] = useState(false);
  const [uniformError, setUniformError] = useState<string>();
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!team) return;
    setForm({
      name: team.name,
      logoUrl: team.logoUrl ?? '',
      primaryColor: team.primaryColor ?? '#10b981',
      secondaryColor: team.secondaryColor ?? '#f59e0b',
      season: team.season ?? '',
      city: team.city ?? '',
      state: team.state ?? '',
      leagueName: team.leagueName ?? '',
      division: team.division ?? '',
      homeFieldName: team.homeFieldName ?? '',
      homeFieldAddress: team.homeFieldAddress ?? '',
      contactEmail: team.contactEmail ?? '',
      contactPhone: team.contactPhone ?? '',
      adminNotes: team.adminNotes ?? '',
      active: team.active,
    });
  }, [team]);

  const loadUniforms = async () => {
    if (!teamId) return;
    try {
      const res = await admin.listUniforms(teamId);
      setUniforms(res.uniforms);
    } catch (err) {
      setUniformError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void loadUniforms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamId) return;
    setSaving(true);
    setError(undefined);
    try {
      await admin.updateTeam(teamId, {
        name: form.name.trim(),
        primaryColor: nullable(form.primaryColor),
        secondaryColor: nullable(form.secondaryColor),
        season: nullable(form.season),
        city: nullable(form.city),
        state: nullable(form.state),
        leagueName: nullable(form.leagueName),
        division: nullable(form.division),
        homeFieldName: nullable(form.homeFieldName),
        homeFieldAddress: nullable(form.homeFieldAddress),
        contactEmail: nullable(form.contactEmail),
        contactPhone: nullable(form.contactPhone),
        adminNotes: nullable(form.adminNotes),
        active: form.active,
      });
      await refresh();
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file: File | undefined) => {
    if (!teamId || !file) return;
    setUploading(true);
    setError(undefined);
    try {
      const signed = await admin.createAssetUploadUrl(teamId, {
        type: 'teamLogo',
        contentType: file.type,
      });
      const res = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status} ${res.statusText}`);
      const updated = await admin.updateTeam(teamId, { logoKey: signed.key });
      setForm((cur) => ({ ...cur, logoUrl: updated.logoUrl ?? signed.publicUrl ?? '' }));
      await refresh();
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const clearLogo = async () => {
    if (!teamId) return;
    setUploading(true);
    setError(undefined);
    try {
      await admin.updateTeam(teamId, { logoKey: null, logoUrl: null });
      setForm((cur) => ({ ...cur, logoUrl: '' }));
      await refresh();
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const rotate = async () => {
    if (!teamId) return;
    setRotating(true);
    setRotateError(undefined);
    setRotated(undefined);
    try {
      const res = await admin.rotateStreamKey(teamId);
      setRotated(res.streamKey);
    } catch (err) {
      setRotateError(err instanceof Error ? err.message : String(err));
    } finally {
      setRotating(false);
    }
  };

  const remove = async () => {
    if (
      !team ||
      !confirm(
        `Delete team "${team.name}"? This permanently removes the IVS channel, players, opponents, and follower list. Recordings stay in S3.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    setDeleteError(undefined);
    try {
      await admin.deleteTeam(team.teamId);
      navigate('/admin/teams');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
      setDeleting(false);
    }
  };

  const addUniform = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamId) return;
    setSavingUniform(true);
    setUniformError(undefined);
    try {
      await admin.createUniform(teamId, {
        name: uniformForm.name.trim(),
        jerseyColor: nullable(uniformForm.jerseyColor) ?? undefined,
        pantsColor: nullable(uniformForm.pantsColor) ?? undefined,
        hatColor: nullable(uniformForm.hatColor) ?? undefined,
        socksColor: nullable(uniformForm.socksColor) ?? undefined,
        beltColor: nullable(uniformForm.beltColor) ?? undefined,
        notes: nullable(uniformForm.notes) ?? undefined,
        active: true,
      });
      setUniformForm(emptyUniformForm);
      await loadUniforms();
    } catch (err) {
      setUniformError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingUniform(false);
    }
  };

  const setUniformActive = async (uniform: TeamUniform, active: boolean) => {
    if (!teamId) return;
    setUniformError(undefined);
    try {
      await admin.updateUniform(teamId, uniform.uniformId, { active });
      await loadUniforms();
    } catch (err) {
      setUniformError(err instanceof Error ? err.message : String(err));
    }
  };

  if (loadError) return <Text className="text-red-400">Error: {loadError}</Text>;
  if (loading || !team) return <Text>Loading team details...</Text>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading level={2}>Team details</Heading>
          <Text className="mt-1 text-sm">Branding, public team profile, and private staff notes.</Text>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button href={`/teams/${team.teamId}`} outline>
            Public page
          </Button>
          <Button href={`/admin/teams/${team.teamId}/games`} color="dark/zinc">
            Games
          </Button>
        </div>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Panel className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div
              className="flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
              style={{ borderColor: form.primaryColor || undefined }}
            >
              {form.logoUrl ? (
                <img src={form.logoUrl} alt="" className="size-full object-cover" />
              ) : (
                <span className="text-lg font-semibold text-zinc-400">
                  {form.name.slice(0, 2).toUpperCase()}
                </span>
              )}
            </div>
            <div className="grid flex-1 gap-4 sm:grid-cols-2">
              <Field className="sm:col-span-2">
                <Label>Team logo</Label>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    outline
                    disabled={uploading}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {uploading ? 'Uploading...' : 'Upload logo'}
                  </Button>
                  {form.logoUrl ? (
                    <Button
                      type="button"
                      outline
                      disabled={uploading}
                      onClick={() => void clearLogo()}
                    >
                      Remove logo
                    </Button>
                  ) : null}
                  <Text className="text-xs text-zinc-500">PNG, JPG, or WebP</Text>
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={(e) => void uploadLogo(e.target.files?.[0])}
                  disabled={uploading}
                />
              </Field>
              <Field>
                <Label>Primary color</Label>
                <Input
                  type="color"
                  value={form.primaryColor}
                  onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                />
              </Field>
              <Field>
                <Label>Secondary color</Label>
                <Input
                  type="color"
                  value={form.secondaryColor}
                  onChange={(e) => setForm({ ...form, secondaryColor: e.target.value })}
                />
              </Field>
            </div>
          </div>
        </Panel>

        <Panel className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <Label>Team name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field>
              <Label>Season</Label>
              <Input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} />
            </Field>
            <Field>
              <Label>League</Label>
              <Input value={form.leagueName} onChange={(e) => setForm({ ...form, leagueName: e.target.value })} />
            </Field>
            <Field>
              <Label>Division</Label>
              <Input value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} />
            </Field>
            <Field>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Field>
            <Field>
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </Field>
            <Field>
              <Label>Home field</Label>
              <Input value={form.homeFieldName} onChange={(e) => setForm({ ...form, homeFieldName: e.target.value })} />
            </Field>
            <Field>
              <Label>Home field address</Label>
              <Input value={form.homeFieldAddress} onChange={(e) => setForm({ ...form, homeFieldAddress: e.target.value })} />
            </Field>
            <Field>
              <Label>Contact email</Label>
              <Input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </Field>
            <Field>
              <Label>Contact phone</Label>
              <Input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
            </Field>
          </div>
          <Field>
            <Label>Admin notes</Label>
            <Textarea rows={3} value={form.adminNotes} onChange={(e) => setForm({ ...form, adminNotes: e.target.value })} />
          </Field>
          <CheckboxField>
            <Checkbox checked={form.active} onChange={(checked) => setForm({ ...form, active: checked })} />
            <Label>Team is active</Label>
          </CheckboxField>
        </Panel>

        {error ? <ErrorMessage>{error}</ErrorMessage> : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" color="dark/zinc" disabled={saving || uploading}>
            {saving ? 'Saving...' : uploading ? 'Uploading...' : 'Save team details'}
          </Button>
          {savedAt ? <Text className="text-xs text-zinc-500">Saved at {savedAt}</Text> : null}
        </div>
      </form>

      <Panel className="space-y-5">
        <div>
          <Heading level={3} className="text-lg">Uniforms</Heading>
          <Text className="mt-1 text-sm">Team-level presets for games, practices, and events.</Text>
        </div>

        <form onSubmit={addUniform} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            <Field className="sm:col-span-2 lg:col-span-2">
              <Label>Uniform name</Label>
              <Input
                required
                value={uniformForm.name}
                onChange={(e) => setUniformForm({ ...uniformForm, name: e.target.value })}
                placeholder="White jersey / navy hat"
              />
            </Field>
            <Field>
              <Label>Jersey</Label>
              <Input
                type="color"
                value={uniformForm.jerseyColor}
                onChange={(e) => setUniformForm({ ...uniformForm, jerseyColor: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Pants</Label>
              <Input
                type="color"
                value={uniformForm.pantsColor}
                onChange={(e) => setUniformForm({ ...uniformForm, pantsColor: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Hat</Label>
              <Input
                type="color"
                value={uniformForm.hatColor}
                onChange={(e) => setUniformForm({ ...uniformForm, hatColor: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Socks</Label>
              <Input
                type="color"
                value={uniformForm.socksColor}
                onChange={(e) => setUniformForm({ ...uniformForm, socksColor: e.target.value })}
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
            <Field>
              <Label>Notes</Label>
              <Input
                value={uniformForm.notes}
                onChange={(e) => setUniformForm({ ...uniformForm, notes: e.target.value })}
                placeholder="Optional belt, undershirt, or coach note"
              />
            </Field>
            <Button type="submit" color="dark/zinc" disabled={savingUniform}>
              {savingUniform ? 'Adding...' : 'Add uniform'}
            </Button>
          </div>
        </form>

        {uniformError ? <ErrorMessage>{uniformError}</ErrorMessage> : null}

        {uniforms.length === 0 ? (
          <Text className="text-sm">No uniforms set yet.</Text>
        ) : (
          <ul className="grid gap-3 lg:grid-cols-2">
            {uniforms.map((uniform) => (
              <li
                key={uniform.uniformId}
                className="rounded-lg border border-zinc-800 bg-zinc-950 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-100">{uniform.name}</div>
                    <div className="mt-2"><UniformSwatches uniform={uniform} /></div>
                    {uniform.notes ? <Text className="mt-2 text-sm">{uniform.notes}</Text> : null}
                  </div>
                  <Button
                    type="button"
                    outline
                    onClick={() => void setUniformActive(uniform, !uniform.active)}
                  >
                    {uniform.active ? 'Make inactive' : 'Activate'}
                  </Button>
                </div>
                <Text className="mt-2 text-xs">
                  {uniform.active ? 'Active' : 'Inactive'}
                </Text>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel className="space-y-4">
        <div>
          <Heading level={3} className="text-lg">Stream channel</Heading>
          <Text className="mt-1 font-mono text-xs text-zinc-500">
            {team.channelArn?.split('/').pop() ?? '(no channel pinned)'}
          </Text>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void rotate()} disabled={rotating} outline>
            {rotating ? 'Rotating...' : 'Rotate stream key'}
          </Button>
        </div>
        {rotated ? (
          <div className="rounded border border-emerald-700/60 bg-emerald-950/20 p-3">
            <div className="text-xs font-medium text-emerald-200">
              New stream key, shown once:
            </div>
            <code className="mt-1 block break-all font-mono text-sm text-emerald-100">
              {rotated}
            </code>
          </div>
        ) : null}
        {rotateError ? <Text className="text-red-400">{rotateError}</Text> : null}
      </Panel>

      <Panel className="space-y-3 border-red-900/60">
        <Heading level={3} className="text-lg">Danger zone</Heading>
        <Text className="text-sm">
          Delete this team workspace, roster, opponents, and follower list. Recordings stay in S3.
        </Text>
        <Button onClick={() => void remove()} disabled={deleting} color="red">
          {deleting ? 'Deleting...' : 'Delete team'}
        </Button>
        {deleteError ? <Text className="text-red-400">{deleteError}</Text> : null}
      </Panel>
    </div>
  );
}
